/**
 * User progress store (IndexedDB).
 *
 * Holds everything personal and mutable — separate from the read-only clue
 * bank. Card states and tag skills are loaded into memory on startup (bounded
 * by how many clues the user has actually reviewed) and written through on each
 * grade so a crash never loses more than the in-flight answer.
 */
import type { CardState, TagSkill } from '../engine/types';

const DB_NAME = 'jeopardy-progress';
const VERSION = 1;
const CARDS = 'cards';
const SKILLS = 'skills';
const KV = 'kv';

export interface LifetimeStats {
  presented: number;
  known: number;
  shaky: number;
  missed: number;
  /** YYYY-MM-DD -> count, for a streak/heatmap later. */
  daily: Record<string, number>;
}

const EMPTY_STATS: LifetimeStats = { presented: 0, known: 0, shaky: 0, missed: 0, daily: {} };

export class ProgressStore {
  private db!: IDBDatabase;
  cards = new Map<number, CardState>();
  skills = new Map<number, TagSkill>();
  stats: LifetimeStats = structuredClone(EMPTY_STATS);

  async open(): Promise<void> {
    this.db = await new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(CARDS)) db.createObjectStore(CARDS, { keyPath: 'clueId' });
        if (!db.objectStoreNames.contains(SKILLS)) db.createObjectStore(SKILLS, { keyPath: 'tagId' });
        if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    await this.loadAll();
  }

  private loadAll(): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([CARDS, SKILLS, KV], 'readonly');
      const cReq = tx.objectStore(CARDS).getAll();
      const sReq = tx.objectStore(SKILLS).getAll();
      const statReq = tx.objectStore(KV).get('stats');
      tx.oncomplete = () => {
        for (const c of cReq.result as CardState[]) this.cards.set(c.clueId, c);
        for (const s of sReq.result as TagSkill[]) this.skills.set(s.tagId, s);
        if (statReq.result) this.stats = statReq.result as LifetimeStats;
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Write through one card + one or more skills + updated stats in a single tx. */
  persist(card: CardState, skills: TagSkill[]): Promise<void> {
    this.cards.set(card.clueId, card);
    for (const s of skills) this.skills.set(s.tagId, s);
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([CARDS, SKILLS, KV], 'readwrite');
      tx.objectStore(CARDS).put(card);
      const ss = tx.objectStore(SKILLS);
      for (const s of skills) ss.put(s);
      tx.objectStore(KV).put(this.stats, 'stats');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Clue ids whose review is due at or before `now`. */
  dueCardIds(now: number): number[] {
    const out: number[] = [];
    for (const c of this.cards.values()) if (c.due <= now) out.push(c.clueId);
    return out;
  }

  async reset(): Promise<void> {
    this.cards.clear();
    this.skills.clear();
    this.stats = structuredClone(EMPTY_STATS);
    await new Promise<void>((resolve, reject) => {
      const tx = this.db.transaction([CARDS, SKILLS, KV], 'readwrite');
      tx.objectStore(CARDS).clear();
      tx.objectStore(SKILLS).clear();
      tx.objectStore(KV).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
